import { useEffect, useState } from "react";
import { SiteListApiItem } from "../../site-listings/services/siteListingConfig";
import { fetchSEOConfigs } from "../services/seoConfig";
import { fetchSeoPageDefinitions } from "../services/seoPageDefinitions";
import { computeSEOAnalysis } from "./useSEOAnalysis";
import { toForm, EMPTY_FORM } from "../types";

export interface SiteSeoStats {
  pageCount: number;
  score: number;
}

const normalizeKey = (k: string) => k.toLowerCase().trim();

export const useSiteSeoStats = (sites: SiteListApiItem[]) => {
  const [stats, setStats] = useState<Record<number, SiteSeoStats>>({});

  useEffect(() => {
    if (!sites.length) return;
    let cancelled = false;

    Promise.all(
      sites.map((site) =>
        Promise.all([
          fetchSEOConfigs(site.id)
            .then((r) => r.data.results || [])
            .catch(() => []),
          fetchSeoPageDefinitions(site.id)
            .then((r) => r.data.results || [])
            .catch(() => []),
        ]).then(([configs, definitions]) => ({
          siteId: site.id,
          configs,
          definitions,
        })),
      ),
    ).then((allResults) => {
      if (cancelled) return;
      const next: Record<number, SiteSeoStats> = {};

      allResults.forEach(({ siteId, configs, definitions }) => {
        // Merge: defined pages + custom configs without a definition
        // (mirrors the logic in SEOWebsitePages.tsx)
        const definedKeys = new Set(
          definitions.map((d: { page_key: string }) => normalizeKey(d.page_key)),
        );
        const customConfigs = configs.filter(
          (c: { page_name: string }) => !definedKeys.has(normalizeKey(c.page_name)),
        );
        const totalPages = definitions.length + customConfigs.length;

        // Score: mirrors SEOWebsitePages.tsx — configs use real scores,
        // definitions with no config get scored from EMPTY_FORM + pageName
        const configScores = configs.map(
          (config: object) => computeSEOAnalysis(toForm(config as Parameters<typeof toForm>[0])).score,
        );
        const definitionOnlyScores = definitions
          .filter(
            (d: { page_key: string }) =>
              !configs.some(
                (c: { page_name: string }) =>
                  normalizeKey(c.page_name) === normalizeKey(d.page_key),
              ),
          )
          .map((d: { page_key: string }) =>
            computeSEOAnalysis({ ...EMPTY_FORM, pageName: d.page_key }).score,
          );
        const allScores = [...configScores, ...definitionOnlyScores];
        const average =
          allScores.length > 0
            ? Math.round(
                allScores.reduce((sum, score) => sum + score, 0) /
                  allScores.length,
              )
            : 0;

        next[siteId] = { pageCount: totalPages, score: average };
      });

      setStats(next);
    });

    return () => {
      cancelled = true;
    };
  }, [sites]);

  return stats;
};
